import React, { useEffect, useRef, useState } from "react";
import {
  clearCover,
  clearLogo,
  fetchBranding,
  getUser,
  hasFeature,
  resolveImageUrl,
  updateRestaurantSettings,
  uploadCover,
  uploadLogo,
} from "../services/api";
import { useT } from "../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    countryEt: "Ethiopia (EN + አማርኛ + Oromiffaa)",
    countryNl: "Netherlands (EN + Nederlands)",
    countryOther: "Other / English only",
    countryTitle: "Country & languages",
    countryHelp:
      "We use this to decide which language toggle the customer sees when they scan a QR code. Set once during setup — change later only if your menu language mix changes.",
    countryLabel: "Country",
    opsTitle: "Billing options",
    opsHelp: "Turn optional billing actions on or off for your hotel.",
    groupChargeLabel: "Allow charging a tab to a group / transferring bills",
    groupChargeHelp: "When on, cashiers can settle an F&B tab against a group block and move items between tables. Turn off if you don't use group billing.",
    on: "On", off: "Off", errSaveOps: "Could not save the setting.",
    saved: "Saved.",
    errSaveCountry: "Could not save country.",
    saving: "Saving…",
    save: "Save",
    noImage: "No image uploaded yet.",
    uploading: "Uploading…",
    replaceImage: "Replace image",
    uploadImage: "Upload image",
    remove: "Remove",
    pageTitle: "Settings & branding",
    pageHelp:
      "Restaurant-wide settings. Country sets which language toggles your guests see; logo and cover image are available on the Hotel and Enterprise plans and appear on the customer menu, receipt, and rating screens.",
    errLoadBranding: "Could not load branding.",
    errLogoUpload: "Logo upload failed.",
    errCoverUpload: "Cover upload failed.",
    confirmClearLogo: "Remove the logo? Customers will see plain text in headers.",
    confirmClearCover: "Remove the cover image?",
    errClearLogo: "Could not remove logo.",
    errClearCover: "Could not remove cover.",
    logoTitle: "Logo",
    logoHint:
      "Square image, ideally 400×400. Shown at the top of the menu, on the receipt, and on the rating page.",
    coverTitle: "Cover image",
    coverHint:
      "Wide banner, ideally 1600×600. Shown as the hero image on the customer menu page.",
    tipsTitle: "Tips",
    tip1: "Use a transparent-background PNG for the logo so it sits cleanly on any header colour.",
    tip2: "Cover photos look best with the subject (food, lobby, view) centred — both ends get cropped on narrow phones.",
    tip3: "Changes go live instantly. Refresh the customer page (or rescan a QR) to see them.",
    lockedTitle: "Custom logo & cover image",
    lockedBody:
      "Upload your logo and a banner photo so guests see your own brand when they scan a QR code, on the receipt, and on the rating screen.",
    lockedPlanPre: "Available on the ",
    lockedPlanHotel: "Hotel",
    lockedPlanMid: " and ",
    lockedPlanEnt: "Enterprise",
    lockedPlanPost: " plans (subscription or licence).",
    seePlans: "See plans & request quote",
  },
  am: {
    countryEt: "ኢትዮጵያ (EN + አማርኛ + Oromiffaa)",
    countryNl: "ኔዘርላንድስ (EN + Nederlands)",
    countryOther: "ሌላ / እንግሊዝኛ ብቻ",
    countryTitle: "አገር እና ቋንቋዎች",
    countryHelp:
      "ደንበኛው QR ኮድ ሲቃኝ የትኛውን የቋንቋ መቀየሪያ እንደሚያይ ለመወሰን ይህንን እንጠቀማለን። በማዋቀር ጊዜ አንድ ጊዜ ያዘጋጁ — የሜኑ ቋንቋ ድብልቅዎ ከተቀየረ ብቻ በኋላ ይቀይሩ።",
    countryLabel: "አገር",
    saved: "ተቀምጧል።",
    errSaveCountry: "አገርን ማስቀመጥ አልተቻለም።",
    saving: "በማስቀመጥ ላይ…",
    save: "አስቀምጥ",
    noImage: "እስካሁን ምስል አልተሰቀለም።",
    uploading: "በመስቀል ላይ…",
    replaceImage: "ምስል ተካ",
    uploadImage: "ምስል ስቀል",
    remove: "አጥፋ",
    pageTitle: "ቅንብሮች እና ብራንዲንግ",
    pageHelp:
      "በመላው ምግብ ቤት ቅንብሮች። አገር እንግዶችዎ የሚያዩትን የቋንቋ መቀየሪያ ያዘጋጃል፤ ሎጎ እና የሽፋን ምስል በሆቴል እና በኢንተርፕራይዝ እቅዶች ይገኛሉ እና በደንበኛ ሜኑ፣ ደረሰኝ እና በደረጃ መስጫ ማያ ገጾች ላይ ይታያሉ።",
    errLoadBranding: "ብራንዲንግ መጫን አልተቻለም።",
    errLogoUpload: "ሎጎ መስቀል አልተሳካም።",
    errCoverUpload: "ሽፋን መስቀል አልተሳካም።",
    confirmClearLogo: "ሎጎውን ማስወገድ ይፈልጋሉ? ደንበኞች በራስጌዎች ላይ ቀላል ጽሑፍ ያያሉ።",
    confirmClearCover: "የሽፋን ምስሉን ማስወገድ ይፈልጋሉ?",
    errClearLogo: "ሎጎ ማስወገድ አልተቻለም።",
    errClearCover: "ሽፋን ማስወገድ አልተቻለም።",
    logoTitle: "ሎጎ",
    logoHint:
      "ካሬ ምስል፣ በተለይም 400×400። በሜኑ ላይኛው ክፍል፣ በደረሰኝ እና በደረጃ መስጫ ገጽ ላይ ይታያል።",
    coverTitle: "የሽፋን ምስል",
    coverHint:
      "ሰፊ ባነር፣ በተለይም 1600×600። በደንበኛ ሜኑ ገጽ ላይ እንደ ዋና ምስል ይታያል።",
    tipsTitle: "ጠቃሚ ምክሮች",
    tip1: "ሎጎው በማንኛውም የራስጌ ቀለም ላይ በንፁህ እንዲቀመጥ ግልጽ-ዳራ ያለው PNG ይጠቀሙ።",
    tip2: "የሽፋን ፎቶዎች ርዕሰ ጉዳዩ (ምግብ፣ ሎቢ፣ እይታ) በመሃል ሲሆን በተሻለ ይታያሉ — ሁለቱም ጫፎች በጠባብ ስልኮች ላይ ይቆረጣሉ።",
    tip3: "ለውጦች ወዲያውኑ ይተገበራሉ። ለማየት የደንበኛ ገጹን ያድሱ (ወይም QR እንደገና ይቃኙ)።",
    lockedTitle: "ብጁ ሎጎ እና የሽፋን ምስል",
    lockedBody:
      "እንግዶች QR ኮድ ሲቃኙ፣ በደረሰኝ ላይ እና በደረጃ መስጫ ማያ ገጽ ላይ የራስዎን ብራንድ እንዲያዩ ሎጎዎን እና የባነር ፎቶ ይስቀሉ።",
    lockedPlanPre: "በ",
    lockedPlanHotel: "ሆቴል",
    lockedPlanMid: " እና ",
    lockedPlanEnt: "ኢንተርፕራይዝ",
    lockedPlanPost: " እቅዶች ላይ ይገኛል (ምዝገባ ወይም ፍቃድ)።",
    seePlans: "እቅዶችን ይመልከቱ እና ጥቅስ ይጠይቁ",
  },
  om: {
    countryEt: "Itoophiyaa (EN + አማርኛ + Oromiffaa)",
    countryNl: "Nezerlaandis (EN + Nederlands)",
    countryOther: "Kan biroo / Ingiliffa qofa",
    countryTitle: "Biyya & afaanota",
    countryHelp:
      "Yeroo maamilli koodii QR skaanii godhu jijjiirraa afaanii kam akka argu murteessuuf kana fayyadamna. Yeroo qindeessaa al tokko qindeessi — yoo makaan afaan meenuu kee jijjiirame qofa booda jijjiiri.",
    countryLabel: "Biyya",
    saved: "Olkaa'ame.",
    errSaveCountry: "Biyya olkaa'uun hin danda'amne.",
    saving: "Olkaa'aa jira…",
    save: "Olkaa'i",
    noImage: "Hanga ammaatti suuraan hin olkaa'amne.",
    uploading: "Olkaa'aa jira…",
    replaceImage: "Suuraa Bakka Buusi",
    uploadImage: "Suuraa Olkaa'i",
    remove: "Balleessi",
    pageTitle: "Qindaa'inoota & Braandii",
    pageHelp:
      "Qindaa'inoota mana nyaataa guutuu. Biyyi jijjiirraa afaanii keessummoonni kee argan qindeessa; loogoo fi suuraan goolaba karoora Hotelaa fi Enterprise irratti argamu, meenuu maamilaa, nagahee fi iddoo madaallii irratti mul'atu.",
    errLoadBranding: "Braandii fe'uun hin danda'amne.",
    errLogoUpload: "Loogoo olkaa'uun hin milkoofne.",
    errCoverUpload: "Suuraa goolaba olkaa'uun hin milkoofne.",
    confirmClearLogo: "Loogoo haquu? Maamiltoonni mataduree irratti barreeffama qofa argu.",
    confirmClearCover: "Suuraa goolaba haquu?",
    errClearLogo: "Loogoo haquun hin danda'amne.",
    errClearCover: "Suuraa goolaba haquun hin danda'amne.",
    logoTitle: "Loogoo",
    logoHint:
      "Suuraa roga afurii, gaariidha 400×400. Gubbaa meenuu, nagahee fi fuula madaallii irratti mul'ata.",
    coverTitle: "Suuraa Goolaba",
    coverHint:
      "Baaneera bal'aa, gaariidha 1600×600. Fuula meenuu maamilaa irratti akka suuraa gootaatti mul'ata.",
    tipsTitle: "Gorsa",
    tip1: "Loogoof PNG duuback iftoomina qabu fayyadami, akka halluu mataduree kamiyyuu irra qulqulluudhaan taa'uuf.",
    tip2: "Suuraaleen goolaba yeroo wanti (nyaata, lobii, mul'ata) walakkaatti jiru gaarii mul'atu — bilbila dhiphoo irratti rogi lamaanuu ni mura.",
    tip3: "Jijjiiramni battalumatti hojiirra oola. Arguuf fuula maamilaa haaromsi (yookiin QR irra deebi'ii skaani).",
    lockedTitle: "Loogoo & Suuraa Goolaba Mataa kee",
    lockedBody:
      "Loogoo kee fi suuraa baaneeraa olkaa'i, akka keessummoonni yeroo koodii QR skaanii godhan, nagahee irratti, fi iddoo madaallii irratti braandii kee mataa kee argan.",
    lockedPlanPre: "Karoora ",
    lockedPlanHotel: "Hotelaa",
    lockedPlanMid: " fi ",
    lockedPlanEnt: "Enterprise",
    lockedPlanPost: " irratti argama (galmee yookiin hayyama).",
    seePlans: "Karoora ilaali & gatii gaafadhu",
  },
};

function tr(lang, k) {
  return (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
}

// Country options surfaced to the manager. Order matters: NL and ET are
// the markets we explicitly target; OTHER is the fallback that hides
// the language picker on the customer app entirely.
function countryOptions(lang) {
  return [
    { code: "ET", label: tr(lang, "countryEt") },
    { code: "NL", label: tr(lang, "countryNl") },
    { code: "OTHER", label: tr(lang, "countryOther") },
  ];
}

function CountryCard({ initial, onSaved }) {
  const { lang } = useT();
  const t = (k) => tr(lang, k);
  const [value, setValue] = useState(initial || "ET");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedLabel, setSavedLabel] = useState(null);

  useEffect(() => {
    setValue(initial || "ET");
  }, [initial]);

  const dirty = value !== (initial || "ET");

  const save = async () => {
    setBusy(true); setErr(null); setSavedLabel(null);
    try {
      await updateRestaurantSettings({ country: value });
      setSavedLabel(t("saved"));
      onSaved?.(value);
    } catch (e) {
      setErr(
        e?.response?.data?.detail || t("errSaveCountry")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>{t("countryTitle")}</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        {t("countryHelp")}
      </p>
      <div className="field">
        <label htmlFor="country-select">{t("countryLabel")}</label>
        <select
          id="country-select"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        >
          {countryOptions(lang).map((o) => (
            <option key={o.code} value={o.code}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {err && <div className="error">{err}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          type="button"
          className="btn"
          disabled={!dirty || busy}
          onClick={save}
        >
          {busy ? t("saving") : t("save")}
        </button>
        {savedLabel && <span className="muted">{savedLabel}</span>}
      </div>
    </div>
  );
}

// Two-slot uploader card. Reused for both Logo and Cover -- only the
// helper text and aspect-ratio of the preview change.
function BrandingSlot({
  title,
  hint,
  currentUrl,
  uploading,
  error,
  onPick,
  onClear,
  aspect = "1 / 1",
  maxPreviewHeight = 220,
}) {
  const { lang } = useT();
  const t = (k) => tr(lang, k);
  const inputRef = useRef(null);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <p className="muted" style={{ marginTop: 0 }}>{hint}</p>

      <div
        style={{
          background: "#f6f3ec",
          border: "1px dashed #d3c8af",
          borderRadius: 12,
          padding: 18,
          textAlign: "center",
          marginBottom: 12,
          minHeight: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {currentUrl ? (
          <img
            src={resolveImageUrl(currentUrl)}
            alt={title}
            style={{
              maxHeight: maxPreviewHeight,
              maxWidth: "100%",
              aspectRatio: aspect,
              objectFit: "contain",
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
          />
        ) : (
          <span className="muted">{t("noImage")}</span>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPick(f);
            e.target.value = "";
          }}
        />
        <button
          className="btn"
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? t("uploading") : currentUrl ? t("replaceImage") : t("uploadImage")}
        </button>
        {currentUrl && (
          <button
            className="btn danger small"
            type="button"
            disabled={uploading}
            onClick={onClear}
          >
            {t("remove")}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ManagerBrandingPage() {
  const { lang } = useT();
  const t = (k) => tr(lang, k);
  const [branding, setBranding] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [logoErr, setLogoErr] = useState(null);
  const [coverErr, setCoverErr] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    fetchBranding()
      .then(setBranding)
      .catch((e) =>
        setLoadErr(e?.response?.data?.detail || t("errLoadBranding"))
      );
  }, []);

  const handleLogo = async (file) => {
    setLogoBusy(true); setLogoErr(null);
    try {
      const data = await uploadLogo(file);
      setBranding(data);
    } catch (e) {
      setLogoErr(
        e?.response?.data?.detail?.message ||
          e?.response?.data?.detail ||
          t("errLogoUpload")
      );
    } finally {
      setLogoBusy(false);
    }
  };

  const handleCover = async (file) => {
    setCoverBusy(true); setCoverErr(null);
    try {
      const data = await uploadCover(file);
      setBranding(data);
    } catch (e) {
      setCoverErr(
        e?.response?.data?.detail?.message ||
          e?.response?.data?.detail ||
          t("errCoverUpload")
      );
    } finally {
      setCoverBusy(false);
    }
  };

  const handleClearLogo = async () => {
    if (!window.confirm(t("confirmClearLogo")))
      return;
    setLogoBusy(true); setLogoErr(null);
    try {
      await clearLogo();
      setBranding((b) => ({ ...(b || {}), logo_url: null }));
    } catch (e) {
      setLogoErr(e?.response?.data?.detail || t("errClearLogo"));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleClearCover = async () => {
    if (!window.confirm(t("confirmClearCover"))) return;
    setCoverBusy(true); setCoverErr(null);
    try {
      await clearCover();
      setBranding((b) => ({ ...(b || {}), cover_image_url: null }));
    } catch (e) {
      setCoverErr(e?.response?.data?.detail || t("errClearCover"));
    } finally {
      setCoverBusy(false);
    }
  };

  const canCustomBrand = hasFeature("custom_branding");
  const user = getUser();

  return (
    <>
      <h1>{t("pageTitle")}</h1>
      <p className="muted">
        {t("pageHelp")}
      </p>

      {loadErr && <div className="error">{loadErr}</div>}

      {/* Country -- available to ALL tiers, drives language picker */}
      <CountryCard initial={user?.country} />

      {/* Branding -- Business tier only */}
      {canCustomBrand ? (
        <>
          <BrandingSlot
            title={t("logoTitle")}
            hint={t("logoHint")}
            currentUrl={branding?.logo_url}
            uploading={logoBusy}
            error={logoErr}
            aspect="1 / 1"
            maxPreviewHeight={180}
            onPick={handleLogo}
            onClear={handleClearLogo}
          />

          <BrandingSlot
            title={t("coverTitle")}
            hint={t("coverHint")}
            currentUrl={branding?.cover_image_url}
            uploading={coverBusy}
            error={coverErr}
            aspect="3 / 1"
            maxPreviewHeight={220}
            onPick={handleCover}
            onClear={handleClearCover}
          />

          <div className="card" style={{ background: "#fffaf0", borderColor: "#f0d38b" }}>
            <h3 style={{ marginTop: 0 }}>{t("tipsTitle")}</h3>
            <ul style={{ marginBottom: 0, lineHeight: 1.7 }}>
              <li>{t("tip1")}</li>
              <li>{t("tip2")}</li>
              <li>{t("tip3")}</li>
            </ul>
          </div>
        </>
      ) : (
        <div
          className="card"
          style={{ background: "#fffaf0", borderColor: "#f0d38b" }}
        >
          <h3 style={{ marginTop: 0 }}>{t("lockedTitle")}</h3>
          <p style={{ marginBottom: 8 }}>
            {t("lockedBody")}
          </p>
          <p className="muted" style={{ marginBottom: 12 }}>
            {t("lockedPlanPre")}<b>{t("lockedPlanHotel")}</b>{t("lockedPlanMid")}<b>{t("lockedPlanEnt")}</b>{t("lockedPlanPost")}
          </p>
          <a href="/pricing" className="btn">
            {t("seePlans")}
          </a>
        </div>
      )}
    </>
  );
}
